import { cn } from '@/utils/cn';
import { useState } from 'react';

interface StarRatingProps {
  value: number;
  onChange: (rating: number) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  readonly?: boolean;
  error?: boolean;
}

export const StarRating = ({
  value,
  onChange,
  className,
  size = 'md',
  readonly = false,
  error = false,
}: StarRatingProps) => {
  const [hoverValue, setHoverValue] = useState<number>(0);

  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  const handleClick = (rating: number) => {
    if (!readonly) {
      onChange(rating);
    }
  };

  const handleMouseEnter = (rating: number) => {
    if (!readonly) {
      setHoverValue(rating);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoverValue(0);
    }
  };

  const StarIcon = ({ filled, index }: { filled: boolean; index: number }) => (
    <button
      type="button"
      onClick={() => handleClick(index)}
      onMouseEnter={() => handleMouseEnter(index)}
      onMouseLeave={handleMouseLeave}
      disabled={readonly}
      className={cn(
        sizeClasses[size],
        'relative transition-all duration-300 ease-out',
        readonly ? 'cursor-default' : 'cursor-pointer',
        'transform-gpu',
        !readonly && 'hover:-translate-y-1 hover:scale-125',
        filled && !readonly && 'animate-pulse',
      )}
      style={{
        filter: filled
          ? 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.6))'
          : 'none',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        className={cn(
          'absolute inset-0 transition-all duration-300',
          filled
            ? error
              ? 'text-red-400'
              : 'text-yellow-400'
            : 'text-gray-300',
          !readonly && 'group-hover:text-yellow-300',
        )}
        style={{
          filter: filled ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' : 'none',
        }}
      >
        <defs>
          <linearGradient
            id={`starGradient-${index}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop
              offset="0%"
              stopColor={filled ? (error ? '#f87171' : '#fbbf24') : '#d1d5db'}
            />
            <stop
              offset="100%"
              stopColor={filled ? (error ? '#dc2626' : '#f59e0b') : '#9ca3af'}
            />
          </linearGradient>
        </defs>
        <path
          fill={`url(#starGradient-${index})`}
          stroke={filled ? (error ? '#dc2626' : '#f59e0b') : '#9ca3af'}
          strokeWidth="0.5"
          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
          className="transition-all duration-300"
        />
      </svg>

      {/* Sparkle effect for filled stars */}
      {filled && !readonly && (
        <div className="pointer-events-none absolute inset-0">
          <div
            className={cn(
              'absolute size-1 rounded-full bg-white opacity-0',
              'animate-ping',
            )}
            style={{
              top: '20%',
              left: '50%',
              animationDelay: '0ms',
              animationDuration: '2s',
            }}
          />
          <div
            className={cn(
              'absolute size-1 rounded-full bg-white opacity-0',
              'animate-ping',
            )}
            style={{
              top: '50%',
              left: '80%',
              animationDelay: '500ms',
              animationDuration: '2s',
            }}
          />
          <div
            className={cn(
              'absolute size-1 rounded-full bg-white opacity-0',
              'animate-ping',
            )}
            style={{
              top: '70%',
              left: '30%',
              animationDelay: '1000ms',
              animationDuration: '2s',
            }}
          />
        </div>
      )}
    </button>
  );

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {[1, 2, 3, 4, 5].map(star => {
        const isFilled = star <= (hoverValue || value);
        return (
          <div
            key={star}
            className={cn(
              'group relative',
              !readonly && 'transition-transform duration-200 hover:scale-110',
            )}
          >
            <StarIcon filled={isFilled} index={star} />
          </div>
        );
      })}
    </div>
  );
};
